/**
 * The default Prebid configuration for the Odyssey platform.
 *
 * This is applied by the PrebidPlugin to the `window.pbjs.setConfig` method to
 * set the initial configuration for Prebid.js.
 */
export default {
  cache: {
    url: "https://prebid.adnxs.com/pbc/v1/cache",
  },
  debug: false,
  enableTIDs: true,
  enableSendAllBids: true,
  sendBidsControl: {
    bidLimit: 2,
    dealPrioritization: true,
  },
  priceGranularity: "dense",
  targetingControls: {
    alwaysIncludeDeals: true,
    allowTargetingKeys: [
      "AD_ID",
      "BIDDER",
      "PRICE_BUCKET",
      "UUID",
      "SIZE",
      "DEAL",
      "CACHE_HOST",
      "SOURCE",
    ],
    allowSendAllBidsTargetingKeys: [
      "AD_ID",
      "BIDDER",
      "PRICE_BUCKET",
      "UUID",
      "SIZE",
      "DEAL",
    ],
  },
  timeoutBuffer: 400,
  useBidCache: false,
  userSync: {
    filterSettings: {
      iframe: {
        bidders: "*",
        filter: "include",
      },
      image: {
        bidders: "*",
        filter: "include",
      },
    },
    userIds: [] as PrebidUserIds[],
    auctionDelay: 50,
    syncsPerBidder: 3,
    syncDelay: 6000,
  },
  ozone: {
    enhancedAdserverTargeting: false,
    oz_whitelist_adserver_keys: [],
  },
  rubicon: {
    singleRequest: true,
  },
} satisfies PrebidConfig;

/**
 * The default Prebid bidder settings for the Odyssey platform.
 *
 * This is applied by the PrebidPlugin/PrebidWrapper when prebid is initialized.
 */
export const DEFAULT_PREBID_BIDDER_SETTINGS: PrebidBidderSettings = {
  standard: {
    storageAllowed: true,
  },
} satisfies PrebidBidderSettings;

/**
 * NOTE: NOT USED BY DEFAULT, THIS IS APPLIED CASE BY CASE.
 *
 * By default, the Prebid configuration uses the `standard` adserverTargeting
 * configuration to set the key-values for the adserver targeting on all bid
 * responses. These values are then passed to GAM based on the configuration of
 * the `targetingControls` property in the Prebid configuration. This
 * configuration is what applies the `hb_adid`, `hb_bidder`, `hb_pb`, `hb_size`,
 * `hb_source`, and some other `hb_*` key-values to the `adserverTargeting`
 * property on each bid response.
 *
 * When `window.pbjs.bidderSettings.standard.adserverTargeting` has a value, then
 * the automatic `standard` configuration is switched to entirely manual mode,
 * only adding the key-values defined in the `adserverTargeting` array OR that
 * already exist from the bid response itself.
 *
 * This means, that if any third party scripts or plugins modify the targeting
 * configuration, then we will need to apply the `standard` configuration
 * manually to ensure that the targeting values are set correctly, and expected
 * values are not missing and never get sent to the adserver.
 *
 * For example, the Blockthrough Readership Link plugin does this, since their
 * btmessage.com script when loaded, will add two additional key-values to the
 * `adserverTargeting` configuration, `rl_wid` and `bt_mwr` keyvalues.  This
 * will cause the `hb_adid` and `hb_source` key-values to no longer be set
 * automatically for S2S calls. The other keyvalues are set however, because the
 * S2S call responds with the `hb_size`, `hb_bidder`, `hb_pb`, and `hb_deal`
 * key-values, which are then set in the adserverTargeting configuration.
 *
 * ## Usage
 *
 * Only when required, apply this configuration to the `pbjs.bidderSettings` using
 * the `PrebidPlugin.addPrebidReadyCallback` (which will add the provided function to
 * window.pbjs.que):
 *
 * ```typescript
 * import { mergeObjects } from "utils";
 *
 * const MyPlugin extends AdEnginePlugin {
 *  private applyStandardAdserverTargetingConfig() {
 *    this.usePlugin(PrebidPlugin)?.addPrebidReadyCallback(() => {
 *      const pbjs = window.pbjs!;
 *      pbjs.bidderSettings = mergeObjects(
 *        pbjs.bidderSettings,
 *        STANDARD_PREBID_ADSERVER_TARGETING_CONFIG,
 *      ) as PrebidBidderSettings;
 *    });
 *  }
 * }
 * ```
 *
 * @see https://docs.prebid.org/dev-docs/publisher-api-reference/bidderSettings.html#21-adservertargeting
 */
export const STANDARD_PREBID_ADSERVER_TARGETING_CONFIG: PrebidBidderSettings = {
  standard: {
    adserverTargeting: [
      {
        key: "hb_bidder",
        val: function (bidResponse) {
          return bidResponse.bidderCode;
        },
      },
      {
        key: "hb_adid",
        val: function (bidResponse) {
          return bidResponse.adId;
        },
      },
      {
        key: "hb_pb",
        val: function (bidResponse) {
          return bidResponse.pbHg;
        },
      },
      {
        key: "hb_size",
        val: function (bidResponse) {
          return bidResponse.size;
        },
      },
      {
        key: "hb_source",
        val: function (bidResponse) {
          return bidResponse.source;
        },
      },
      {
        key: "hb_format",
        val: function (bidResponse) {
          return bidResponse.mediaType;
        },
      },
      {
        key: "hb_deal",
        val: function (bidResponse) {
          return bidResponse.dealId;
        },
      },
    ],
  },
} satisfies PrebidBidderSettings;
